import type { EstimateStatus, QuickCategory } from 'used-vehicle-contracts';
import type { BadgeTone } from '../../components/ui/Badge';
import type { StatusTone } from '../../components/ui/StatusText';

/**
 * How an estimate is *shown* — tones, labels and the small formatting decisions the pages share.
 *
 * Colour discipline (plan §2.6): an estimate waiting on a customer is `warning`, one ready for the
 * salesperson to act on is `info`, a purchase is `success`, a lost deal is `danger`, and a draft — the
 * ordinary, expected state — is left muted. Anything coloured on these pages is therefore genuinely
 * worth looking at.
 */
export const ESTIMATE_STATUS_TONES: Record<EstimateStatus, StatusTone> = {
  DRAFT: 'muted',
  READY_TO_OFFER: 'info',
  OFFER_SHARED: 'warning',
  DEAL_AGREED: 'success',
  NO_DEAL: 'danger',
};

export const estimateStatusTone = (status: EstimateStatus): StatusTone => ESTIMATE_STATUS_TONES[status] ?? 'muted';

export const ESTIMATE_STATUSES: readonly EstimateStatus[] = ['DRAFT', 'READY_TO_OFFER', 'OFFER_SHARED', 'DEAL_AGREED', 'NO_DEAL'];

/** The four quick-range benchmarks in the order the prototype lists them (plan §5.11). */
export const QUICK_CATEGORIES: readonly QuickCategory[] = ['GOOD_LOW', 'GOOD_HIGH', 'POOR_LOW', 'POOR_HIGH'];

/**
 * The best case is `success`, the worst is `danger`, the two in between are `warning` — so a
 * salesperson reading the four cards at a glance sees the shape of the range, not four identical tiles.
 */
export const QUICK_CATEGORY_TONES: Record<QuickCategory, BadgeTone> = {
  GOOD_LOW: 'success',
  GOOD_HIGH: 'warning',
  POOR_LOW: 'warning',
  POOR_HIGH: 'danger',
};

export const quickCategoryTone = (key: QuickCategory): BadgeTone => QUICK_CATEGORY_TONES[key] ?? 'warning';

/** The three steps of the estimator; step 3 is a placeholder until the offer lands in the next phase. */
export const ESTIMATOR_STEPS = [1, 2, 3] as const;

export type EstimatorStep = (typeof ESTIMATOR_STEPS)[number];

export const clampStep = (value: number): EstimatorStep => (value <= 1 ? 1 : value >= 3 ? 3 : 2);

/** "Toyota Corolla HEV" from whatever parts are recorded, without stray separators. */
export const vehicleTitle = (parts: { make?: string | null; model?: string | null }): string =>
  [parts.make, parts.model].filter((part): part is string => typeof part === 'string' && part.trim() !== '').join(' ');

/** Severity selects and the grade select share the same "unknown first" ordering. */
export const GRADE7_VALUES = ['UNKNOWN', 'G7', 'G4', 'G3', 'G2', 'G1', 'G0'] as const;

export const SEVERITIES = ['UNKNOWN', 'EXCELLENT', 'MINOR', 'MODERATE', 'MAJOR', 'SEVERE'] as const;

export const FUELS = ['GAS', 'HYBRID', 'EV', 'DIESEL'] as const;

export const ORIGINS = ['AGENCY', 'GCC', 'IMPORTED', 'OTHER'] as const;

export const USAGES = ['PRIVATE', 'FLEET', 'RENTAL', 'TAXI'] as const;

export const SERVICE_HISTORIES = ['UNKNOWN', 'AGENCY_FULL', 'THIRD_PARTY_AGENCY', 'THIRD_PARTY_DOCUMENTED', 'PARTIAL', 'NONE'] as const;

/** The powertrains that have a hybrid or EV system to assess at all (plan §7 step 2). */
export const hasHybridSystem = (fuel: string | null | undefined): boolean => fuel === 'HYBRID' || fuel === 'EV';

/** `<input type="number">` gives back a string; an emptied field means "not recorded", never zero. */
export const toOptionalNumber = (value: string): number | null => {
  const trimmed = value.trim();

  if (trimmed === '') {
    return null;
  }

  const parsed = Number(trimmed);

  return Number.isFinite(parsed) ? parsed : null;
};

/** `<input type="text">` likewise: an emptied field clears the column rather than storing `''`. */
export const toOptionalText = (value: string): string | null => {
  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
};

/** A tri-state answer: `''` = not answered, which is never the same as "No" (PP §7.1). */
export const toTriState = (value: string): boolean | null => (value === '' ? null : value === 'true');

export const fromTriState = (value: boolean | null | undefined): string => (value === null || value === undefined ? '' : String(value));
